import React from 'react';
import { Box, getVariant } from '@apia/theme';
import ContextMenu from './ContextMenu';
import Item from './Item';
import { useTabsContext } from './util';
import { getLabel, getSpecificParent } from '@apia/util';
import { IconButton } from '../components';
import { observer } from 'mobx-react-lite';
import { useShowScrollButtons } from './hooks/useShowScrollButtons';

export const TabsList = observer(
  ({ arrowsBehavior = 'focus' }: { arrowsBehavior?: 'open' | 'focus' }) => {
    const controller = useTabsContext();
    const [showScrollButtons, setRef] = useShowScrollButtons();

    const onClickPrev = React.useCallback(() => {
      if (arrowsBehavior === 'focus') controller?.focusPreviousTab();
      if (arrowsBehavior === 'open') controller?.openPreviousTab();
    }, [arrowsBehavior, controller]);

    const onClickNext = React.useCallback(() => {
      if (arrowsBehavior === 'focus') controller?.focusNextTab();
      if (arrowsBehavior === 'open') controller?.openNextTab();
    }, [arrowsBehavior, controller]);

    const [anchorPoint, setAnchorPoint] = React.useState({ x: 0, y: 0 });
    const [isOpen, setIsOpen] = React.useState(false);
    const [targetId, setTargetId] = React.useState<string>('');

    if (!controller) return null;

    const tabs = controller.tabsList;

    const fixedTabs = (tabs ?? []).filter((current) => current.state.isFixed);
    const nonFixedTabs = (tabs ?? []).filter(
      (current) => !current.state.isFixed,
    );

    return (
      <Box
        id={`tabsList-${controller.id}`}
        className="tabsList__wrapper"
        role="tablist"
        {...getVariant('layout.common.tabs.primary')}
        ref={setRef}
      >
        <Box className="tabsList__fixedTabs">
          {fixedTabs.map((current) => (
            <Item key={current.id} tab={current} />
          ))}
        </Box>
        <ContextMenu
          anchorPoint={anchorPoint}
          isOpen={isOpen}
          onClose={React.useCallback(() => setIsOpen(false), [])}
          targetId={targetId}
        />
        <Box
          className={`noScrollbar tabs__list ${
            controller.props.orientation === 'vertical' ? 'vertical' : ''
          }`}
          onContextMenu={React.useCallback((e: React.MouseEvent) => {
            const tabElement = getSpecificParent(
              e.target as HTMLElement,
              (current) => current.classList.contains('tabs__item__wrapper'),
            );
            if (tabElement) {
              e.preventDefault();
              e.stopPropagation();
              setTargetId(tabElement.dataset.id as string);
              setAnchorPoint({ x: e.clientX, y: e.clientY });
              setIsOpen(true);
            }
          }, [])}
        >
          {nonFixedTabs.map((current) => (
            <Item key={current.id} tab={current} />
          ))}
        </Box>
        {showScrollButtons && (
          <>
            <IconButton
              aria-label={getLabel('lblTogglePrevTab').text}
              className="tabs__switch__left"
              icon="ArrowLeft"
              iconSize="Xs"
              onClick={onClickPrev}
              title={getLabel('lblTogglePrevTab').text}
              {...getVariant('icon-primary')}
            />
            <IconButton
              aria-label={getLabel('lblToggleNextTab').text}
              className="tabs__switch__right"
              icon="ArrowRight"
              iconSize="Xs"
              onClick={onClickNext}
              title={getLabel('lblToggleNextTab').text}
              {...getVariant('icon-primary')}
            />
          </>
        )}
      </Box>
    );
  },
);
